import { useEffect, useId, type ReactNode } from 'react';
import type { DestinationNode, OrderDraft, PaymentMode } from '@mypoz/shared';
import { findDestination, postcodeWarning } from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { Card, Select } from '@mypoz/ui';

import type { CatalogFetchStatus } from '@/features/orders/catalogContext.js';
import type { ServicesView } from '@/features/orders/useScopedServices.js';
import {
  PromoCodeField,
  type PromoCodeFieldProps,
} from '@/features/orders/components/PromoCodeField.js';
import { ServiceCard } from '@/components/ServiceCard.js';

export interface DeliveryFormProps {
  /** Top-level destinations only — provinces and cities are loaded per parent, below. */
  countries: readonly DestinationNode[];
  provincesFor: (countryCode: string) => readonly DestinationNode[];
  provincesStatus: (countryCode: string) => CatalogFetchStatus;
  citiesFor: (provinceCode: string) => readonly DestinationNode[];
  citiesStatus: (provinceCode: string) => CatalogFetchStatus;
  loadCities: (provinceCode: string) => void;
  /** The services card's content — see `useScopedServices` (MYP-89). */
  servicesView: ServicesView;
  /** Set when ticked services were cleared because the order's warehouse does not offer them. */
  servicesCleared?: boolean;
  draft: OrderDraft;
  onPatch: (patch: Partial<OrderDraft>) => void;
  /**
   * Shows the promo code, and the payment method when `payment` is given, read-only, each with the
   * reason given here. Editing an order locks both: `order_modify` takes neither, so an edit to
   * them would be dropped on save (MYP-48). A new order locks only the promo code, which cannot be
   * checked before packing (MYP-95); its payment method is the customer's choice.
   */
  locked?: { readonly payment?: string; readonly coupon: string };
  /**
   * The promo code field for a new order (MYP-95). When given, it replaces the plain coupon input;
   * Edit order leaves it out and shows the order's code read-only instead.
   */
  promo?: PromoCodeFieldProps;
}

/**
 * A dropdown with its label (the shared `Select` since MYP-137).
 *
 * `loading` disables the field the same way `disabled` does (there is nothing to pick yet either
 * way) but says why, since "greyed out with no explanation" and "greyed out because its parent
 * hasn't been chosen" read as the same thing without a hint the fetch is what's pending.
 */
function SelectField({
  label,
  value,
  placeholder,
  loadingLabel,
  options,
  disabled,
  loading,
  note,
  onChange,
}: {
  label: string;
  value: string | undefined;
  placeholder: string;
  loadingLabel: string;
  options: readonly { readonly code: string; readonly name: string; readonly disabled?: boolean }[];
  disabled: boolean;
  loading?: boolean;
  /** Said under the field — why it is empty, or what to do about it. */
  note?: ReactNode;
  onChange: (code: string | undefined) => void;
}) {
  const id = useId();

  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm text-muted">
        {label}
      </label>
      <Select
        id={id}
        value={value ?? ''}
        onChange={(code) => onChange(code || undefined)}
        options={options.map((option) => ({
          value: option.code,
          label: option.name,
          disabled: option.disabled,
        }))}
        placeholder={loading ? loadingLabel : placeholder}
        disabled={disabled || loading}
      />
      {note ? <div className="mt-1.5 text-sm text-ink">{note}</div> : null}
    </div>
  );
}

/** A labelled text input. `required` here marks the field visually; the step gate does the rest. */
function TextField({
  label,
  value,
  placeholder,
  autoComplete,
  inputMode,
  optional,
  hint,
  onChange,
}: {
  label: string;
  value: string;
  placeholder?: string;
  autoComplete: string;
  inputMode?: 'tel' | 'numeric';
  optional?: string;
  /** A warning under the field. Never blocks anything — see `postcodeWarning`. */
  hint?: string;
  onChange: (value: string) => void;
}) {
  const id = useId();
  const hintId = useId();

  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm text-muted">
        {label}
        {optional ? <span className="ml-1.5 text-xs text-muted-soft">{optional}</span> : null}
      </label>
      <input
        id={id}
        type="text"
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete}
        inputMode={inputMode}
        aria-describedby={hint ? hintId : undefined}
        onChange={(event) => onChange(event.target.value)}
        className="min-h-11 w-full rounded-md border border-line bg-surface px-3 text-sm text-ink transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand lg:min-h-10"
      />
      {hint ? (
        <p
          id={hintId}
          role="status"
          className="mt-1.5 border-l-2 border-warning pl-2 text-sm text-ink"
        >
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Step 3 — where the order goes, what the warehouse should do to it, and how it gets paid.
 *
 * The destination is a cascade rather than three independent pickers: choosing a country clears
 * the province and city beneath it, because a Selangor left over from Malaysia is not a valid
 * Singaporean address, and a stale value that still *looks* chosen is worse than an empty field.
 *
 * Below the cascade sits the rest of a real address. A country, state and city name a place; they
 * do not name a door. Collecting only the cascade was what left MYP-43's detail panel with a
 * recipient it could render but nothing to put in it, and a courier cannot deliver to a city.
 */
export function DeliveryForm({
  countries,
  provincesFor,
  provincesStatus,
  citiesFor,
  citiesStatus,
  loadCities,
  servicesView,
  servicesCleared = false,
  draft,
  onPatch,
  locked,
  promo,
}: DeliveryFormProps) {
  const paymentLocked = locked?.payment !== undefined;
  const couponLocked = locked !== undefined;
  const { t } = useTranslation();

  const country = findDestination(countries, draft.countryCode);
  const provinces = draft.countryCode ? provincesFor(draft.countryCode) : [];
  const province = findDestination(provinces, draft.provinceCode);
  const cities = draft.provinceCode ? citiesFor(draft.provinceCode) : [];

  // States arrive with the catalog (MYP-88). Cities are fetched the moment their state is chosen,
  // not on select-open — the field renders its own "loading" placeholder in the meantime (see
  // `SelectField`). A no-op once `loadCities` has been asked for that state, so revisiting this
  // step (Confirm's Edit link, the step strip) never re-fetches.
  useEffect(() => {
    if (draft.provinceCode) loadCities(draft.provinceCode);
  }, [draft.provinceCode, loadCities]);

  const citiesState = draft.provinceCode ? citiesStatus(draft.provinceCode) : 'idle';

  /*
    A state with exactly one city — every state outside Malaysia, live — has nothing to choose, so
    it is chosen. Only into an empty city: an existing order's city is never replaced.
  */
  const onlyCity = citiesState === 'ready' && cities.length === 1 ? cities[0] : undefined;
  useEffect(() => {
    if (onlyCity && draft.cityCode === undefined) onPatch({ cityCode: onlyCity.code });
  }, [onlyCity, draft.cityCode, onPatch]);

  const countryOptions = countries.map((option) =>
    option.available === false
      ? {
          code: option.code,
          name: t('orders.newOrder.delivery.countryUnavailable', { name: option.name }),
          disabled: true,
        }
      : option,
  );

  const postcode = postcodeWarning(
    draft.postcode,
    country,
    findDestination(cities, draft.cityCode),
  );
  const postcodeHint =
    postcode?.kind === 'length'
      ? postcode.min === postcode.max
        ? t('orders.newOrder.delivery.postcodeLength', { count: postcode.min })
        : t('orders.newOrder.delivery.postcodeLengthRange', {
            min: postcode.min,
            max: postcode.max,
          })
      : postcode?.kind === 'range'
        ? t('orders.newOrder.delivery.postcodeRange', {
            city: findDestination(cities, draft.cityCode)?.name ?? '',
            ranges: postcode.ranges
              .map((range) => (range.from === range.to ? range.from : `${range.from}–${range.to}`))
              .join(', '),
          })
        : undefined;

  const toggleService = (serviceId: string) =>
    onPatch({
      serviceIds: draft.serviceIds.includes(serviceId)
        ? draft.serviceIds.filter((id) => id !== serviceId)
        : [...draft.serviceIds, serviceId],
    });

  const setPaymentMode = (paymentMode: PaymentMode) => onPatch({ paymentMode });

  return (
    <div className="grid gap-5">
      <Card>
        <h2 className="text-base font-semibold text-ink">
          {t('orders.newOrder.delivery.address')}
        </h2>

        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <SelectField
            label={t('orders.newOrder.delivery.country')}
            value={draft.countryCode}
            placeholder={t('orders.newOrder.delivery.selectCountry')}
            loadingLabel={t('orders.newOrder.delivery.loadingOptions')}
            options={countryOptions}
            disabled={false}
            note={
              country?.available === false
                ? t('orders.newOrder.delivery.countryUnavailableNote')
                : undefined
            }
            onChange={(code) =>
              onPatch({ countryCode: code, provinceCode: undefined, cityCode: undefined })
            }
          />
          <SelectField
            label={t('orders.newOrder.delivery.province')}
            value={draft.provinceCode}
            placeholder={t('orders.newOrder.delivery.selectProvince')}
            loadingLabel={t('orders.newOrder.delivery.loadingOptions')}
            options={provinces}
            disabled={!country}
            loading={!!draft.countryCode && provincesStatus(draft.countryCode) === 'loading'}
            onChange={(code) => onPatch({ provinceCode: code, cityCode: undefined })}
          />
          <SelectField
            label={t('orders.newOrder.delivery.city')}
            value={draft.cityCode}
            placeholder={t('orders.newOrder.delivery.selectCity')}
            loadingLabel={t('orders.newOrder.delivery.loadingOptions')}
            options={cities}
            disabled={!province}
            loading={citiesState === 'loading'}
            note={
              citiesState === 'error' ? (
                <span className="flex flex-wrap items-center gap-x-2">
                  <span>{t('orders.newOrder.delivery.citiesFailed')}</span>
                  <button
                    type="button"
                    onClick={() => draft.provinceCode && loadCities(draft.provinceCode)}
                    className="font-semibold text-brand hover:text-brand-dark focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                  >
                    {t('orders.newOrder.delivery.retry')}
                  </button>
                </span>
              ) : citiesState === 'ready' && cities.length === 0 ? (
                t('orders.newOrder.delivery.noCities')
              ) : undefined
            }
            onChange={(code) => onPatch({ cityCode: code })}
          />
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <TextField
            label={t('orders.newOrder.delivery.recipient')}
            value={draft.recipient}
            autoComplete="name"
            onChange={(recipient) => onPatch({ recipient })}
          />
          <TextField
            label={t('orders.newOrder.delivery.phone')}
            value={draft.phone}
            autoComplete="tel"
            inputMode="tel"
            placeholder={t('orders.newOrder.delivery.phonePlaceholder')}
            onChange={(phone) => onPatch({ phone })}
          />
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-[2fr_1fr]">
          <TextField
            label={t('orders.newOrder.delivery.line1')}
            value={draft.line1}
            autoComplete="street-address"
            placeholder={t('orders.newOrder.delivery.line1Placeholder')}
            onChange={(line1) => onPatch({ line1 })}
          />
          {/*
            Marked optional rather than gated: several destinations here do not use postcodes, and
            a required field nobody in Singapore can fill is a dead end, not a validation.
          */}
          <TextField
            label={t('orders.newOrder.delivery.postcode')}
            value={draft.postcode}
            autoComplete="postal-code"
            inputMode="numeric"
            optional={t('orders.newOrder.delivery.optional')}
            hint={postcodeHint}
            onChange={(next) => onPatch({ postcode: next })}
          />
        </div>
      </Card>

      <Card className="p-0">
        <div className="border-b border-line px-5 py-4 sm:px-6">
          <h2 className="text-base font-semibold text-ink">
            {t('orders.newOrder.delivery.services')}
          </h2>
        </div>

        {servicesView.kind === 'noWarehouse' ? (
          <p className="px-5 py-4 text-sm text-muted sm:px-6">
            {t('orders.newOrder.delivery.servicesNoWarehouse')}
          </p>
        ) : servicesView.kind === 'loading' ? (
          <p className="px-5 py-4 text-sm text-muted sm:px-6">
            {t('orders.newOrder.delivery.servicesLoading')}
          </p>
        ) : servicesView.kind === 'error' ? (
          <p className="flex flex-wrap items-center gap-x-2 px-5 py-4 text-sm text-ink sm:px-6">
            <span>{t('orders.newOrder.delivery.servicesFailed')}</span>
            <button
              type="button"
              onClick={servicesView.retry}
              className="font-semibold text-brand hover:text-brand-dark focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            >
              {t('orders.newOrder.delivery.retry')}
            </button>
          </p>
        ) : servicesView.offered.length === 0 && servicesView.kept.length === 0 ? (
          <p className="px-5 py-4 text-sm text-muted sm:px-6">
            {t('orders.newOrder.delivery.servicesNone')}
          </p>
        ) : (
          <div className="divide-y divide-line">
            {servicesCleared ? (
              <p role="status" className="px-5 py-3 text-sm text-ink sm:px-6">
                <span className="border-l-2 border-warning pl-3">
                  {t('orders.newOrder.delivery.servicesCleared')}
                </span>
              </p>
            ) : null}

            <div className="grid gap-3 px-5 py-4 sm:px-6">
              {servicesView.offered.map((service) => {
                const isTicked = draft.serviceIds.includes(service.id);

                return (
                  <ServiceCard
                    key={service.id}
                    imageUrl={service.imageUrl}
                    name={service.name}
                    nameNative={service.nameNative}
                    description={service.description}
                    feeCents={service.feeCents}
                    currency={service.currency}
                    priceFallback={t('orders.newOrder.delivery.pricedAfterPacking')}
                    checked={isTicked}
                    onToggle={() => toggleService(service.id)}
                    /*
                      Shown only once the service is ticked: the warehouse confirms the final charge
                      after inspecting the goods, so the price shown is where it starts.
                    */
                    note={
                      isTicked && service.requiresConfirmation
                        ? t('orders.newOrder.delivery.confirmNote')
                        : undefined
                    }
                  />
                );
              })}
            </div>

            {/*
              Services already on the order that its warehouse no longer offers (MYP-89): shown
              ticked, under the order's own name for them, so they are visible and can be removed —
              but once unticked they are gone from this list, because they cannot be added back.
            */}
            {servicesView.kept
              .filter((service) => draft.serviceIds.includes(service.id))
              .map((service) => (
                <label
                  key={service.id}
                  className="flex cursor-pointer gap-3 px-5 py-4 sm:gap-4 sm:px-6"
                >
                  <input
                    type="checkbox"
                    checked
                    onChange={() => toggleService(service.id)}
                    className="mt-0.5 size-5 shrink-0 accent-brand"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-ink">{service.name}</span>
                    <span className="mt-0.5 block text-sm text-muted">
                      {t('orders.newOrder.delivery.serviceNoLongerOffered')}
                    </span>
                  </span>
                </label>
              ))}
          </div>
        )}
      </Card>

      <Card>
        <h2 className="text-base font-semibold text-ink">
          {t('orders.newOrder.delivery.payment')}
        </h2>

        <fieldset className="mt-3 grid gap-3">
          <legend className="sr-only">{t('orders.newOrder.delivery.payment')}</legend>

          {(['MANUAL', 'AUTO'] as const).map((mode) => (
            <label
              key={mode}
              className={
                paymentLocked
                  ? 'flex items-start gap-3 opacity-60'
                  : 'flex cursor-pointer items-start gap-3'
              }
            >
              <input
                type="radio"
                name="payment-mode"
                checked={draft.paymentMode === mode}
                disabled={paymentLocked}
                onChange={() => setPaymentMode(mode)}
                className="mt-0.5 size-5 shrink-0 accent-brand"
              />
              <span className="text-sm text-ink">
                {mode === 'MANUAL'
                  ? t('orders.newOrder.delivery.paymentManual')
                  : t('orders.newOrder.delivery.paymentAuto')}
              </span>
            </label>
          ))}
        </fieldset>

        {locked?.payment ? <p className="mt-3 text-sm text-muted">{locked.payment}</p> : null}
      </Card>

      <Card className="grid gap-4">
        {promo ? (
          <PromoCodeField {...promo} />
        ) : (
          <div>
            <label htmlFor="coupon-code" className="mb-1.5 block text-sm text-muted">
              {t('orders.newOrder.delivery.coupon')}
            </label>
            <input
              id="coupon-code"
              type="text"
              value={draft.couponCode}
              readOnly={couponLocked}
              aria-describedby={couponLocked ? 'coupon-code-locked' : undefined}
              onChange={(event) => onPatch({ couponCode: event.target.value })}
              placeholder={t('orders.newOrder.delivery.couponPlaceholder')}
              className="min-h-11 w-full rounded-md border border-line bg-surface px-3 text-sm text-ink transition-colors read-only:bg-surface-alt read-only:text-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand lg:min-h-10"
            />
            {locked ? (
              <p id="coupon-code-locked" className="mt-1.5 text-sm text-muted">
                {locked.coupon}
              </p>
            ) : null}
          </div>
        )}

        <div>
          <label htmlFor="order-remark" className="mb-1.5 block text-sm text-muted">
            {t('orders.newOrder.delivery.remark')}
          </label>
          <textarea
            id="order-remark"
            value={draft.remark}
            rows={3}
            maxLength={500}
            onChange={(event) => onPatch({ remark: event.target.value })}
            placeholder={t('orders.newOrder.delivery.remarkPlaceholder')}
            className="w-full rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          />
        </div>
      </Card>
    </div>
  );
}
